import type { Point, SceneNode } from "../scene/model";
import { polygonCornerLimit } from "./polygon-corners";

export function supportsCorners(node: SceneNode) {
  return (
    node.kind === "rectangle" ||
    node.kind === "image" ||
    node.kind === "polygon"
  );
}
export function cornerLimit(node: SceneNode) {
  return node.kind === "polygon"
    ? polygonCornerLimit(node)
    : Math.min(node.geometry.width!, node.geometry.height!) / 2;
}
export function cornerRadius(node: SceneNode) {
  return supportsCorners(node)
    ? Math.min(Number(node.geometry.cornerRadius ?? 0), cornerLimit(node))
    : 0;
}
export function assertCorners(node: SceneNode) {
  const r = node.geometry.cornerRadius;
  if (r === undefined) return;
  if (
    !supportsCorners(node) ||
    typeof r !== "number" ||
    !Number.isFinite(r) ||
    r < 0 ||
    r > 1e6
  )
    throw new Error("Недопустимый радиус скругления.");
}
/** Each object clamps the user's request independently, without changing its frame or media. */
export function changeCorners(nodes: readonly SceneNode[], radius: number) {
  if (!Number.isFinite(radius) || radius < 0 || radius > 1e6)
    throw new Error("Радиус должен быть неотрицательным числом до 1000000 px.");
  return nodes.filter(supportsCorners).map((node) => {
    const next = Math.min(radius, cornerLimit(node));
    if (next === cornerRadius(node)) return node;
    return { ...node, geometry: { ...node.geometry, cornerRadius: next } };
  });
}
/** Exact signed distance in local pixels, shared by fill hits and centered strokes. */
export function roundedBoxDistance(node: SceneNode, p: Point) {
  const r = cornerRadius(node),
    x = Math.abs(p.x) - node.geometry.width! / 2 + r,
    y = Math.abs(p.y) - node.geometry.height! / 2 + r;
  return (
    Math.hypot(Math.max(x, 0), Math.max(y, 0)) + Math.min(Math.max(x, y), 0) - r
  );
}
/** Circular corner samples for the GPU stroke only; hit testing/bounds remain analytic. */
export function roundedBoxOutline(node: SceneNode, segments: number): Point[] {
  const r = cornerRadius(node),
    w = node.geometry.width! / 2,
    h = node.geometry.height! / 2;
  const points: Point[] = [];
  const count = Math.max(4, Math.ceil(segments / 4));
  for (let corner = 0; corner < 4; corner++) {
    const cx = corner === 0 || corner === 3 ? w - r : -w + r;
    const cy = corner < 2 ? h - r : -h + r;
    for (let i = 0; i <= count; i++) {
      const angle = ((corner + i / count) * Math.PI) / 2;
      const p = { x: cx + r * Math.cos(angle), y: cy + r * Math.sin(angle) };
      const last = points.at(-1);
      if (!last || Math.hypot(p.x - last.x, p.y - last.y) > 1e-9)
        points.push(p);
    }
  }
  if (
    points.length > 1 &&
    Math.hypot(
      points[0]!.x - points.at(-1)!.x,
      points[0]!.y - points.at(-1)!.y,
    ) < 1e-9
  )
    points.pop();
  return points;
}
